import {IActionable} from "back/commons/actionables";
import {ActionBtn, OActionBtnInit} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {TxtSelMgr} from "back/edit/wed/features/txtSel";
import {WedEditAction} from "back/edit/wed/wedEditor";
import {ITxtRootContext, ITxtTableContext, TxtCellModel, TxtColModel, TxtRowModel, TxtTableModel} from "back/edit/wed/wedlets/txt/txt";
import {InsertParaSibling, ITxtAction, registerTxtAction, TxtInsertAction} from "back/edit/wed/wedlets/txt/txtActions";
import {TxtCell, TxtCol, TxtRow, TxtTable} from "back/edit/wed/wedlets/txt/txtTags";
import {findTxtEltParent} from "back/edit/wed/wedlets/txt/txtUtils";
import {ACTION, Action, ActionMenu, EButtonUiContext, EnumEntryAction, IActionMenu, IActionToggle} from "lib/commons/actions";
import {REG} from 'lib/commons/registry';
import {DOM} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IJmlObj, IJmlSet} from "lib/commons/xml/jml";
import {IXAddr, IXAddrRange, XA} from "lib/commons/xml/xAddr";
import {IDocBatch} from "lib/edit/docHolder";
import {WEDLET} from "back/edit/wed/wedlets/wedlet";

/** Insertion d'un tableau. */
class InsertTable extends TxtInsertAction<ITxtRootContext> implements IActionMenu<ITxtRootContext> {

	constructor() {
		super();
		this._label = (ctx: ITxtRootContext): string => {
			return ctx.txtRoot.getTxtWedModel(this.tagName, this.role).nodeLabel;
		};
	}

	isVisible(ctx: ITxtRootContext) {
		if (getTableFromSel(ctx.txtRoot.selMgrAsIs)) return false;
		return super.isVisible(ctx);
	}

	isMenu(ctx: ITxtRootContext): this is IActionMenu<ITxtRootContext> {return true}

	getDatas(api: 'menu', ctx: ITxtTableContext): Action<ITxtRootContext>[] {
		return [new CountCells(false, ctx.txtRoot.getTxtWedModel(this.tagName, this.role) as TxtTableModel, 10, 6)];
	}

}

registerTxtAction("insertTable", InsertTable);


/** Passe en mode édition des tables. */
export class OpenTableLayout extends Action<ITxtRootContext> implements ITxtAction<ITxtRootContext>, IActionToggle<ITxtRootContext> {

	initFromWed(conf: Element) {}

	/** Invisible si pas dans table pour switch graphique avec le btn d'insertion de tables. */
	isVisible(ctx: ITxtRootContext) {
		if (!getTableFromSel(ctx.txtRoot.selMgrAsIs)) return false;
		return super.isVisible(ctx);
	}

	initButtonNode(buttonNode: Element, ctx: ITxtRootContext) {
		(buttonNode as ActionBtn<ITxtRootContext>).accel = ACTION.getLocalizedKey("Insert"); //Touche "Insert" Hard codé
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		const table = getTableFromSel(ctx.txtRoot.selMgr);
		if (table) table.tableLayout.toggle();
	}

}

registerTxtAction("openTableLayout", OpenTableLayout);

/** Abandonne le mode édition des tables. A n'utiliser qua dans la ttolbar d'une table. */
export class CloseTableLayout extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext>, IActionToggle<ITxtTableContext> {

	initFromWed(conf: Element) {}

	initButtonNode(buttonNode: Element, ctx: ITxtTableContext) {
		(buttonNode as ActionBtn<ITxtTableContext>).accel = ACTION.getLocalizedKey("Insert"); //Touche "Insert" Hard codé
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txTable.tableLayout.closeTableLayout();
	}

}

registerTxtAction("closeTableLayout", CloseTableLayout);


class InsertRow extends TxtInsertAction<ITxtTableContext> implements IActionMenu<ITxtTableContext> {

	public maxEntries: number;

	constructor() {
		super();
		this._label = (ctx: ITxtTableContext): string => {
			return ctx.txTable.getTxtWedModel(this.tagName, this.role).nodeLabel;
		};
	}

	initFromWed(elt: Element) {
		super.initFromWed(elt);
		this.maxEntries = parseInt(elt.getAttribute("max"), 10) || 10;
	}

	isMenu(ctx: ITxtTableContext): this is IActionMenu<ITxtTableContext> {return true}

	getDatas(api: 'menu', ctx: ITxtTableContext): Action<ITxtTableContext>[] {
		const rect = ctx.logicTable.getSelRect();
		const model = ctx.txTable.getTxtWedModel(this.tagName, this.role) as TxtRowModel;
		if (rect.endRow === ctx.logicTable.logicRows.length) {
			return [
				new SelectBeforeAfter(false, model, this.maxEntries, 1).setLabel("Insérer avant la sélection"),
				new SelectBeforeAfter(true, model, this.maxEntries, 1).setLabel("Insérer en fin de tableau")
			]
		} else {
			return [new CountCells(false, model, this.maxEntries, 1)];
		}
	}
}

registerTxtAction("insertRow", InsertRow);


class SelectBeforeAfter extends ActionMenu<ITxtTableContext> {
	constructor(after: boolean, public model: TxtTableModel | TxtRowModel | TxtColModel, public maxRows: number, public maxCols: number) {
		super(after ? 'after' : 'before');
	}

	getActions(ctx: ITxtTableContext): Action<ITxtTableContext>[] {return [new CountCells(this.getId() === 'after', this.model, this.maxRows, this.maxCols)]}
}

class CountCells extends Action<ITxtTableContext> {
	countRows: number;
	countCols: number;

	constructor(public after: boolean, public model: TxtTableModel | TxtRowModel | TxtColModel, public maxRows: number, public maxCols: number) {
		super(after ? 'after' : 'before');
	}

	buildCustomButton(ctx: ITxtTableContext, uiContext: EButtonUiContext, parent?: Element): Element {
		return new CountCellsBtn().initialize({
			reg: REG.findReg(ctx.txtRoot),
			action: this,
			actionContext: ctx,
			uiContext: uiContext,
			role: 'button',
			skin: 'txt-countcells-btn'
		});
	}

	async execute(ctx: ITxtTableContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		if (this.model instanceof TxtTableModel) {
			//Insertion de tableau
			const ctn = InsertParaSibling.findContainer(ctx, selMgr);
			if (ctn) {
				let jml: IJmlSet;
				if (this.model.hasOnCreateHook) {
					jml = await this.model.callOnCreateHook(ctn);
					if (!jml || TxtInsertAction.isInsertPointLost(ctx, ctn, selMgr)) return;//abandon
				} else {
					jml = [this.model.newJml()];
				}
				let content: IJmlSet;
				if (jml.length > 1) {
					content = jml[1] as IJmlSet;
				} else {
					jml.push(content = []);
				}
				const colM = this.model.colModels[0];
				const colTpl = colM.newJml() as IJmlObj;
				colM.widthMgr(ctx.txtRoot.wedMgr).fillDefault(colM, colTpl);
				for (let c = 0; c < this.countCols; c++) content.push(colTpl);
				const rowM = this.model.rowModels[0];
				const cellM = rowM.cellModels[0];
				const cellTpl = cellM.newJml();
				for (let r = 0; r < this.countRows; r++) {
					content.push(rowM.newJml());
					const rowContent = [] as IJmlSet;
					for (let c = 0; c < this.countCols; c++) rowContent.push(cellTpl);
					content.push(rowContent);
				}
				InsertParaSibling.doInsert(jml, ctx, selMgr, ev);
			}
		} else {
			//Insertion de colonnes ou lignes.
			const tableLayout = ctx.txTable.tableLayout;
			const rect = ctx.logicTable.getSelRect();
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(newCellRange(tableLayout.focusCell, tableLayout.anchorCell));
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			if (this.model instanceof TxtColModel) {
				ctx.logicTable.insertCols(batch, this.after ? ctx.txTable.countCols() : ctx.logicTable.getSelRect().startCol, this.model, this.countCols);
			} else {
				ctx.logicTable.insertRows(batch, this.after ? ctx.logicTable.logicRows.length : ctx.logicTable.getSelRect().startRow, this.model, this.countRows);
			}
			batch.doBatch();
		}
	}
}

class CountCellsBtn extends ActionBtn<ITxtTableContext> {

	// @ts-ignore
	action: CountCells;

	lastCell: HTMLElement;

	_initialize(init: OActionBtnInit<ITxtTableContext>) {
		super._initialize(init);
		const sh = this.shadowRoot;
		if (this.action.maxCols === 1) {
			const section = sh.appendChild(document.createElement('section'));
			section.classList.add("rows");
			for (let i = 0; i < this.action.maxRows; i++) section.appendChild(document.createElement('div'));
			this._label = "Nb. de lignes";
			this.setCurrentCell(section.querySelector('div'));
		} else if (this.action.maxRows === 1) {
			const section = sh.appendChild(document.createElement('section'));
			section.classList.add("cols");
			for (let i = 0; i < this.action.maxCols; i++) section.appendChild(document.createElement('div'));
			this._label = "Nb. de colonnes";
			this.setCurrentCell(section.querySelector('div'));
		} else {
			const table = sh.appendChild(document.createElement('table'));
			//for (let i = 0; i < this.action.maxCols; i++) sh.appendChild(document.createElement('col'));
			for (let i = 0; i < this.action.maxRows; i++) {
				const tr = table.appendChild(document.createElement('tr'));
				for (let c = 0; c < this.action.maxRows; c++) tr.appendChild(document.createElement('td'));
			}
			this._label = "Dim. du tableau";
			this.setCurrentCell(table.querySelector('td'));
		}
		this.shadowRoot.addEventListener('mousemove', this.onMouseMove);
		this.addEventListener('keydown', this.onKeydown);
	}

	protected onMouseMove(this: ShadowRoot, ev: MouseEvent) {
		if ((ev.target as Element).localName === "div" || (ev.target as Element).localName === "td") (this.host as CountCellsBtn).setCurrentCell(ev.target as HTMLElement);
	}

	protected onKeydown(ev: KeyboardEvent) {
		switch (ev.key) {
		case 'ArrowLeft':
			const prev = window.getComputedStyle(this).direction || 'ltr' ? this.lastCell.previousSibling : this.lastCell.nextSibling;
			if (prev) {
				this.setCurrentCell(prev as HTMLElement);
				ev.stopImmediatePropagation();
			}
			break;
		case 'ArrowUp':
			if (this.lastCell.localName === 'td') {
				const row = this.lastCell.parentNode.previousSibling;
				if (row) this.setCurrentCell(row.childNodes.item(DOM.computeOffset(this.lastCell)) as HTMLElement);
			} else {
				this.setCurrentCell(this.lastCell.previousSibling as HTMLElement);
			}
			ev.stopImmediatePropagation();
			break;
		case 'ArrowRight':
			const next = window.getComputedStyle(this).direction || 'ltr' ? this.lastCell.nextSibling : this.lastCell.previousSibling;
			if (next) {
				this.setCurrentCell(next as HTMLElement);
				ev.stopImmediatePropagation();
			}
			break;
		case 'ArrowDown':
			if (this.lastCell.localName === 'td') {
				const row = this.lastCell.parentNode.nextSibling;
				if (row) this.setCurrentCell(row.childNodes.item(DOM.computeOffset(this.lastCell)) as HTMLElement);
			} else {
				this.setCurrentCell(this.lastCell.nextSibling as HTMLElement);
			}
			ev.stopImmediatePropagation();
			break;
		}
	}

	protected setCurrentCell(elt: HTMLElement) {
		if (!elt || elt === this.lastCell) return;
		this.lastCell = elt;
		DOM.addClass(this.lastCell, "sel");
		DOM.findPreviousSibling(this.lastCell, ((n: Element) => {DOM.addClass(n, "sel")}) as any);
		DOM.findNextSibling(this.lastCell, ((n: Element) => {DOM.removeClass(n, "sel")}) as any);
		if (this.lastCell.localName === "td") {
			const limitRow = this.lastCell.parentNode as Element;
			this.action.countCols = DOM.computeOffset(this.lastCell, 0, (n: Node): n is Node => DOM.IS_element(n) && n.localName === 'td') + 1;
			this.action.countRows = DOM.computeOffset(limitRow, 0, (n: Node): n is Node => DOM.IS_element(n) && n.localName === 'tr') + 1;
			if (this._labelElt) this._labelElt.textContent = this.action.countCols + " x " + this.action.countRows;
			let row = limitRow.previousElementSibling;
			while (row) {
				let cell = row.firstElementChild;
				for (let i = 0; i < this.action.countCols; i++) {
					DOM.addClass(cell, "sel");
					cell = cell.nextElementSibling;
				}
				while (cell) {
					DOM.removeClass(cell, "sel");
					cell = cell.nextElementSibling;
				}
				row = row.previousElementSibling;
			}
			row = limitRow.nextElementSibling;
			while (row) {
				DOM.findFirstChild(row, ((n: Element) => {DOM.removeClass(n, "sel")}) as any);
				row = row.nextElementSibling;
			}
		} else {
			const count = DOM.computeOffset(this.lastCell, 0, (n: Node): n is Node => DOM.IS_element(n) && n.localName === 'div') + 1;
			if (this.action.maxCols === 1) {
				this.action.countRows = count;
			} else {
				this.action.countCols = count;
			}
			if (this._labelElt) this._labelElt.textContent = count.toFixed(0);
		}
	}

}

customElements.define('txt-countcells-btn', CountCellsBtn);

REG.reg.registerSkin('txt-countcells-btn', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	table {
		table-layout: fixed;
		border-collapse: collapse;
	}

	.rows {
		display: flex;
		min-height: 0;
		min-width: 8em;
		flex-direction: column;
		border: 1px solid var(--border-color);
	}

	.cols {
		display: flex;
		min-height: 0;
		min-width: 0;
		height: 5em;
		border: 1px solid var(--border-color);
	}

	td {
		width: .8em;
		height: .8em;
		background-color: white;
		border: 1px solid var(--border-color);
	}

	div {
		flex: 1;
		min-height: .8em;
		min-width: .8em;
		background-color: white;
		border: 1px solid var(--border-color);
	}

	.sel {
		background-color: darkblue;
	}

	.label {
		text-align: center;
		font-size: var(--label-size);
	}
`);


class InsertCol extends InsertRow {

	getDatas(api: 'menu', ctx: ITxtTableContext): Action<ITxtTableContext>[] {
		const rect = ctx.logicTable.getSelRect();
		const model = ctx.txTable.getTxtWedModel(this.tagName, this.role) as TxtColModel;
		if (rect.endCol === ctx.txTable.countCols()) {
			return [
				new SelectBeforeAfter(false, model, 1, this.maxEntries).setLabel("Insérer avant la sélection"),
				new SelectBeforeAfter(true, model, 1, this.maxEntries).setLabel("Insérer en fin de tableau")
			]
		} else {
			return [new CountCells(false, model, 1, this.maxEntries)];
		}
	}
}

registerTxtAction("insertCol", InsertCol);


class DeleteRows extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		if (!super.isEnabled(ctx)) return false;
		const rect = ctx.logicTable.getSelRect();
		return ctx.logicTable.logicRows.length > rect.endRow - rect.startRow; //on ne peut suppr toutes les rows
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		const tableLayout = ctx.txTable.tableLayout;
		const rect = ctx.logicTable.getSelRect();
		const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(newCellRange(tableLayout.focusCell, tableLayout.anchorCell));
		batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
		ctx.logicTable.deleteRows(batch, rect.startRow, rect.endRow - rect.startRow);
		batch.setSelAfterRange(newCellRange(ctx.logicTable.getTxtCellAt(rect.startRow, 0), ctx.logicTable.getTxtCellAt(rect.startRow, ctx.txTable.countCols() - 1)));
		batch.doBatch();
	}
}

registerTxtAction("deleteRows", DeleteRows);


class DeleteCols extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		if (!super.isEnabled(ctx)) return false;
		const rect = ctx.logicTable.getSelRect();
		return ctx.txTable.countCols() > rect.endCol - rect.startCol;
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		const tableLayout = ctx.txTable.tableLayout;
		const rect = ctx.logicTable.getSelRect();
		const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(newCellRange(tableLayout.focusCell, tableLayout.anchorCell));
		batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
		ctx.logicTable.deleteCols(batch, rect.startCol, rect.endCol - rect.startCol);
		batch.doBatch();
	}
}

registerTxtAction("deleteCols", DeleteCols);

class DeleteArray extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx);
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		ctx.txtRoot.selMgr.restoreSel();
		const tableLayout = ctx.txTable.tableLayout;
		const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(newCellRange(tableLayout.focusCell, tableLayout.anchorCell));
		batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
		batch.deleteSequence(ctx.txTable.wedAnchor, 1);
		batch.doBatch();
	}
}

registerTxtAction("deleteArray", DeleteArray);

class MergeCells extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		if (!super.isEnabled(ctx)) return false;
		const tableLayout = ctx.txTable.tableLayout;
		if (tableLayout.focusCell === tableLayout.anchorCell) return false;
		//On évalue si l'angle supérieur gauche est une cell réelle (algo à revoir pour le permettre sinon).
		const focusOffs = ctx.logicTable.getLogicOffsets(tableLayout.focusCell);
		const anchorOffs = ctx.logicTable.getLogicOffsets(tableLayout.anchorCell);
		const masterCell = ctx.logicTable.getLocicCellAt(Math.min(focusOffs.offsRow, anchorOffs.offsRow), Math.min(focusOffs.offsCol, anchorOffs.offsCol));
		//console.log("masterCell::::", masterCell, masterCell instanceof MasterCell || masterCell instanceof TxtCell)
		return (masterCell instanceof MasterCell || masterCell instanceof TxtCell);
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		const tableLayout = ctx.txTable.tableLayout;
		const focusOffs = ctx.logicTable.getLogicOffsets(tableLayout.focusCell);
		const anchorOffs = ctx.logicTable.getLogicOffsets(tableLayout.anchorCell);
		const startRow = Math.min(focusOffs.offsRow, anchorOffs.offsRow);
		const startCol = Math.min(focusOffs.offsCol, anchorOffs.offsCol);
		const masterCell = ctx.logicTable.getLocicCellAt(startRow, startCol);
		if (masterCell instanceof MasterCell || masterCell instanceof TxtCell) {
			const masterTxtCell = masterCell instanceof MasterCell ? masterCell.txtCell : masterCell;
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(newCellRange(tableLayout.focusCell, tableLayout.anchorCell));
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			const endRow = Math.max(focusOffs.offsRow + tableLayout.focusCell.getRowSpan(), anchorOffs.offsRow + tableLayout.anchorCell.getRowSpan());
			const endCol = Math.max(focusOffs.offsCol + tableLayout.focusCell.getColSpan(), anchorOffs.offsCol + tableLayout.anchorCell.getColSpan());
			ctx.logicTable.mergeCells(batch, masterTxtCell, endRow - startRow, endCol - startCol);
			batch.setSelAfter(masterTxtCell.wedAnchor);
			batch.doBatch();
		}
	}
}

registerTxtAction("mergeCells", MergeCells);


class UnmergeCells extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		if (!super.isEnabled(ctx)) return false;
		//On cherche si au moins cell réelle est splittable dans la sel.
		const rect = ctx.logicTable.getSelRect();
		for (let r = rect.endRow - 1; r >= rect.startRow; r--) {
			const lRow = ctx.logicTable.logicRows[r];
			for (let c = rect.endCol - 1; c >= rect.startCol; c--) {
				if (lRow[c] instanceof MasterCell) return true;
			}
		}
		return false;
	}

	execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		const tableLayout = ctx.txTable.tableLayout;
		const rect = ctx.logicTable.getSelRect();
		const rg = newCellRange(tableLayout.focusCell, tableLayout.anchorCell);
		const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(rg);
		batch.setSelAfterRange(XA.cloneRange(rg));
		batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
		for (let r = rect.endRow - 1; r >= rect.startRow; r--) {
			const lRow = ctx.logicTable.logicRows[r];
			for (let c = rect.endCol - 1; c >= rect.startCol; c--) {
				const lCell = lRow[c];
				if (lCell instanceof MasterCell) ctx.logicTable.mergeCells(batch, lCell.txtCell, 1, 1);
			}
		}
		batch.doBatch();
	}
}

registerTxtAction("unmergeCells", UnmergeCells);


class SetRoleRow extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	initFromWed(elt: Element) {}

	initButtonNode(buttonNode: ActionBtn<ITxtTableContext>, ctx: ITxtTableContext) {
		//buttonNode.setAttribute('role', 'menu');
		ctx.wedMgr.reg.installSkin("txt-table/enumAction", buttonNode.shadowRoot);
		buttonNode.setAttribute("role", "menu");
		if (this._label) buttonNode.title = this._label as string;
		buttonNode._hideLabel = false;
	}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx);
	}


	getLabel(ctx: ITxtTableContext): string {
		const rect = ctx.logicTable.getSelRect();
		const label = ctx.logicTable.logicRows[rect.startRow].txtRow.model.nodeLabel;
		for (let r = rect.startRow + 1; r < rect.endRow; r++) {
			if (label !== ctx.logicTable.logicRows[r].txtRow.model.nodeLabel) return "\u00A0";
		}
		return label;
	}

	async execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		let rect = ctx.logicTable.getSelRect();
		const usedRoles: Dict<number> = {};
		for (let r = rect.startRow; r < rect.endRow; r++) {
			const row = ctx.logicTable.logicRows[r].txtRow;
			if (usedRoles[row.txtRole]) usedRoles[row.txtRole]++;
			else usedRoles[row.txtRole] = 1;
		}
		const total = rect.endRow - rect.startRow;
		const roles: EnumEntryAction<ITxtTableContext>[] = [];
		for (const m of ctx.txTable.model.rowModels) {
			if (m.role == null) continue; // row générique sans role.
			const count = usedRoles[m.role] || 0;
			roles.push(new EnumEntryAction<ITxtTableContext>(m.role, m.nodeLabel, count === 0 ? false : count === total ? true : null));
		}

		const result = await POPUP.showPopupActionsFromEvent({actions: roles, actionContext: ctx, restoreFocus: ev.target as HTMLElement}, ev) as EnumEntryAction<ITxtTableContext>;
		if (result && ctx.txTable.isConnected) {
			// ! async => modif concurrentes possibles, on refresh tout.
			const tableLayout = ctx.txTable.tableLayout;
			const logicTable = tableLayout.logicTable;
			rect = logicTable.getSelRect();
			const rg = newCellRange(tableLayout.focusCell, tableLayout.anchorCell);
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(rg);
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			for (let r = rect.startRow; r < rect.endRow; r++) {
				const row = ctx.logicTable.logicRows[r].txtRow;
				if (row.txtRole !== result.getId()) batch.setAttr(XA.append(row.wedAnchor, row.model.roleAttName), result.getId());
			}
			batch.setSelAfterRange(rg);
			batch.doBatch();
		}
	}

}

registerTxtAction("setRoleRow", SetRoleRow);

REG.reg.registerSkin('txt-table/enumAction', 1, /* language=CSS */ `
	.label {
		max-width: 6em;
		white-space: nowrap;
		font-size: var(--label-size);
	}
`);

class SetRoleCol extends SetRoleRow {

	getLabel(ctx: ITxtTableContext): string {
		const rect = ctx.logicTable.getSelRect();
		const label = ctx.txTable.getCol(rect.startCol).model.nodeLabel;
		for (let c = rect.startCol + 1; c < rect.endCol; c++) {
			if (label !== ctx.txTable.getCol(c).model.nodeLabel) return "\u00A0";
		}
		return label;
	}

	async execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		let rect = ctx.logicTable.getSelRect();
		const usedRoles: Dict<number> = {};
		for (let c = rect.startCol; c < rect.endCol; c++) {
			const col = ctx.txTable.getCol(c);
			if (usedRoles[col.txtRole]) usedRoles[col.txtRole]++;
			else usedRoles[col.txtRole] = 1;
		}
		const total = rect.endCol - rect.startCol;
		const roles: EnumEntryAction<ITxtTableContext>[] = [];
		for (const m of ctx.txTable.model.colModels) {
			if (m.role == null) continue; // col générique sans role.
			const count = usedRoles[m.role] || 0;
			roles.push(new EnumEntryAction<ITxtTableContext>(m.role, m.nodeLabel, count === 0 ? false : count === total ? true : null));
		}

		const result = await POPUP.showPopupActionsFromEvent({actions: roles, actionContext: ctx, restoreFocus: ev.target as HTMLElement}, ev) as EnumEntryAction<ITxtTableContext>;
		if (result && ctx.txTable.isConnected) {
			// ! async => modif concurrentes possibles, on refresh tout.
			const tableLayout = ctx.txTable.tableLayout;
			const logicTable = tableLayout.logicTable;
			rect = logicTable.getSelRect();
			const rg = newCellRange(tableLayout.focusCell, tableLayout.anchorCell);
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(rg);
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			for (let c = rect.startCol; c < rect.endCol; c++) {
				const col = ctx.txTable.getCol(c);
				if (col.txtRole !== result.getId()) batch.setAttr(XA.append(col.wedAnchor, col.model.roleAttName), result.getId());
			}
			batch.setSelAfterRange(rg);
			batch.doBatch();
		}
	}

}

registerTxtAction("setRoleCol", SetRoleCol);


class SetRoleCell extends SetRoleRow {

	getLabel(ctx: ITxtTableContext): string {
		const tableLayout = ctx.txTable.tableLayout;
		const label = tableLayout.focusCell.model.nodeLabel;
		if (tableLayout.focusCell === tableLayout.anchorCell) return label;
		const rect = ctx.logicTable.getSelRect();
		for (let r = rect.startRow; r < rect.endRow; r++) {
			const lRow = ctx.logicTable.logicRows[r];
			for (let c = rect.startCol; c < rect.endCol; c++) {
				const lC = lRow[c];
				if (lC instanceof TxtCell) {
					if (label !== lC.model.nodeLabel) return "\u00A0";
				} else if (lC instanceof MasterCell) {
					if (label !== lC.txtCell.model.nodeLabel) return "\u00A0";
				}
			}
		}
		return label;
	}

	async execute(ctx: ITxtTableContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		let rect = ctx.logicTable.getSelRect();
		const usedRoles: Dict<number> = {};
		const rowsM = new Set<TxtRowModel>();
		const cellsM = new Map<string, TxtCellModel>();
		let total = 0;
		for (let r = rect.startRow; r < rect.endRow; r++) {
			const lRow = ctx.logicTable.logicRows[r];
			for (let c = rect.startCol; c < rect.endCol; c++) {
				const lC = lRow[c];
				const txtC = lC instanceof TxtCell ? lC : lC instanceof MasterCell ? lC.txtCell : null;
				if (txtC) {
					if (usedRoles[txtC.txtRole]) usedRoles[txtC.txtRole]++;
					else usedRoles[txtC.txtRole] = 1;
					total++;
				}
			}
			if (!rowsM.has(lRow.txtRow.model)) {
				rowsM.add(lRow.txtRow.model);
				for (const m of lRow.txtRow.model.cellModels) if (m.role != null) cellsM.set(m.role, m);
			}
		}
		const roles: EnumEntryAction<ITxtTableContext>[] = [];
		for (const m of cellsM.values()) {
			if (m.role == null) continue; // col générique sans role.
			const count = usedRoles[m.role] || 0;
			roles.push(new EnumEntryAction<ITxtTableContext>(m.role, m.nodeLabel, count === 0 ? false : count === total ? true : null));
		}

		const result = await POPUP.showPopupActionsFromEvent({actions: roles, actionContext: ctx, restoreFocus: ev.target as HTMLElement}, ev) as EnumEntryAction<ITxtTableContext>;
		if (result && ctx.txTable.isConnected) {
			//! async => modif concurrentes possibles, on refresh tout.
			const tableLayout = ctx.txTable.tableLayout;
			const logicTable = tableLayout.logicTable;
			rect = logicTable.getSelRect();
			const rg = newCellRange(tableLayout.focusCell, tableLayout.anchorCell);
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(rg);
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			for (let r = rect.startRow; r < rect.endRow; r++) {
				const lRow = ctx.logicTable.logicRows[r];
				for (let c = rect.startCol; c < rect.endCol; c++) {
					const lC = lRow[c];
					const txtC = lC instanceof TxtCell ? lC : lC instanceof MasterCell ? lC.txtCell : null;
					if (txtC && txtC.txtRole !== result.getId()) batch.setAttr(XA.append(txtC.wedAnchor, txtC.model.roleAttName), result.getId());
				}
			}
			batch.setSelAfterRange(rg);
			batch.doBatch();
		}
	}

}

registerTxtAction("setRoleCell", SetRoleCell);

/**
 * Edite la largeur des colonnes.
 * ATTENTION A ne pas utiliser sur des tableaux dont les ITxtColWidthMgr des différents
 * types de colonnes ne seraient pas identiques.
 */
class SetWidthCol extends WedEditAction<ITxtTableContext> implements ITxtAction<ITxtTableContext> {

	_description = "Largeur de(s) colonne(s)";

	initFromWed(elt: Element) {}

	buildCustomButton(ctx: ITxtTableContext, uiContext: EButtonUiContext, parent?: Element): Element {
		return new WidthColBtn().initialize({
			reg: REG.findReg(ctx.txtRoot),
			action: this,
			actionContext: ctx,
			uiContext: uiContext,
			role: 'button',
			skinOver: 'txt-widthcol-btn'
		});
	}

	isEnabled(ctx: ITxtTableContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx);
	}

	setWidth(ctx: ITxtTableContext, width: number) {
		try {
			//ctx.txtRoot.selMgr.restoreSel(); NON input de size doit garder le focus.
			const rect = ctx.logicTable.getSelRect();
			let col = ctx.txTable.getCol(rect.startCol);
			const colWidthMgr = col.widthMgr;
			const bounds = colWidthMgr.getLogicBounds(col);
			const min = bounds.min || 1;
			if (width < min) width = min;
			if (bounds.max && width > bounds.max) width = bounds.max;
			const step = bounds.step || 1;
			width = Math.round(width / step) * step;

			const tableLayout = ctx.txTable.tableLayout;
			const rg = newCellRange(tableLayout.focusCell, tableLayout.anchorCell);
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(rg);
			batch.setMeta(TxtSelMgr.MSGMETA_tblLayout, true);
			colWidthMgr.updateLogicW(col, width, batch);
			for (let c = rect.startCol + 1; c < rect.endCol; c++) {
				col = DOM.findNextSibling(col, TXTTABLE.IS_TxtCol);
				colWidthMgr.updateLogicW(col, width, batch);
			}
			batch.setSelAfterRange(rg);
			ctx.wedMgr.freezeFocus = true;
			batch.doBatch();
		} finally {
			ctx.wedMgr.freezeFocus = false;
		}
	}
}

registerTxtAction("setWidthCol", SetWidthCol);

class WidthColBtn extends ActionBtn<ITxtTableContext> {

	// @ts-ignore
	action: SetWidthCol;

	protected _input: HTMLInputElement;

	protected avrgWidth: number;

	protected _refresh() {
		const txtTable = this._actionContext.txTable;
		const logicTable = this._actionContext.logicTable;
		const rect = logicTable.getSelRect();
		const firstCol = txtTable.getCol(rect.startCol);
		const firstColWidthMgr = firstCol.widthMgr;
		const firstColWidth = firstColWidthMgr.getLogicW(firstCol);
		let avrgWidth: number;
		if (rect.startCol < rect.endCol - 1) {
			//Plus d'une col, on évalue si width !=
			let totalW = firstColWidth;
			let col = firstCol;
			for (let c = rect.startCol + 1; c < rect.endCol; c++) {
				col = DOM.findNextSibling(col, TXTTABLE.IS_TxtCol);
				const w = col.widthMgr.getLogicW(col);
				if (w !== firstColWidth) avrgWidth = 0;
				totalW += w;
			}
			if (avrgWidth === 0) avrgWidth = totalW / (rect.endCol - rect.startCol);
		}
		if (avrgWidth > 0) {
			//On affiche le bouton d'égalisation des largeurs.
			const step = firstColWidthMgr.getLogicBounds(firstCol).step || 1;
			this.avrgWidth = Math.round(avrgWidth / step) * step;
			this._label = "~" + this.avrgWidth;
			this._hideLabel = false;
			if (this._input) {
				if (this._input === DOMSH.findDocumentOrShadowRoot(this._input).activeElement) this.focus();
				DOM.setHidden(this._input, true);
			}
		} else {
			//On affiche l'input de saisie de largeur de colonnes.
			this._hideLabel = true;
			this._label = "";
			this.avrgWidth = 0;
			if (!this._input) {
				const bounds = firstColWidthMgr.getLogicBounds(firstCol);
				this._input = document.createElement('input');
				this._input.type = "number";
				if (bounds.step) this._input.step = bounds.step.toString(10);
				this._input.min = (bounds.min || 1).toString(10);
				if (bounds.max) this._input.max = bounds.max.toString(10);
				if (bounds.step) this._input.step = bounds.step.toString(10);
				(this._input as any as IActionable<any>).actionContext = this._actionContext; //pour TableLayout.onBlur().
				if (!this._labelElt) this._createLabel();
				this._labelElt.insertAdjacentElement('beforebegin', this._input);
				//this.addEventListener('focus', this.onFocus);
				this._input.addEventListener('input', this.onChange);
				this._input.addEventListener('keydown', this.onKeydown);
			}
			this._input.valueAsNumber = firstColWidth;
			DOM.setHidden(this._input, false);
		}
		super._refresh();
	}

	protected _executeAction(ev: Event) {
		if (this._action.isAvailable(this.actionContext)) {
			if (this._input && !this._input.hidden) this._input.focus(); //On transfert le focus sur l'input si il est actif.
			else if (this.avrgWidth > 0) this.action.setWidth(this.actionContext, this.avrgWidth);
			//this.dispatchEvent(new CustomEvent('c-actioned', {detail: {actionable: this}, bubbles: true, composed: true}));
		}
	}

	//onFocus() {
	//On transfert le focus sur l'input si il est actif.
	//if (this._input && !this._input.hidden) this._input.focus();
	//}

	onChange(this: HTMLInputElement) {
		const btn = DOMSH.findHost(this) as WidthColBtn;
		btn.action.setWidth(btn.actionContext, this.valueAsNumber);
	}

	onKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		switch (ev.key) {
		case 'Escape':
			(DOMSH.findHost(this) as HTMLElement).focus();
			break;
		case 'ArrowLeft':
		case 'ArrowRight':
			//pour bloquer la nav dans la toolbar.
			ev.stopImmediatePropagation();
		}
	}
}

customElements.define('txt-widthcol-btn', WidthColBtn);

REG.reg.registerSkin('txt-widthcol-btn', 1, /* language=CSS */ `
	.label, input {
		width: 4em;
		white-space: nowrap;
		font-size: var(--label-size);
		border: 1px solid var(--border-color);
		padding: 0;
	}
`);


function getTableFromSel(selMgr: TxtSelMgr): TxtTable {
	return getTableFromNode(selMgr.focusNode) || (selMgr.type === 'Range' && getTableFromNode(selMgr.anchorNode));
}

function getTableFromNode(from: Node): TxtTable {
	let parent = findTxtEltParent(from, true);
	while (parent) {
		if (parent instanceof TxtTable) return parent;
		parent = findTxtEltParent(parent);
	}
	return null;
}


export namespace TXTTABLE {

	export const IS_TxtCol = (n: Node): n is TxtCol => n instanceof TxtCol;
	export const IS_TxtRow = (n: Node): n is TxtRow => n instanceof TxtRow;
	export const IS_TxtCell = (n: Node): n is TxtCell => n instanceof TxtCell;
	export const IS_TxtTable = (n: Node): n is TxtTable => n instanceof TxtTable;

	/**
	 * Représentation logique du tableau ou chaque cellule logique est représentée par
	 * le noeud TxtCell pour les cellules simples, MasterCell pour les cellules possédant
	 * une propriété rowspan ou colspan, et MergedCell pour les cellules fusionnées.
	 *
	 * Exemple, la table :
	 *
	 * <tr><td><td rowspan=2 colspan=3><td></tr>
	 * <tr><td><td></tr>
	 * <tr><td><td><td><td><td></tr>
	 *
	 * sera modélisé dans le LogicTable :
	 *
	 * TxtCell MasterCell MergedCell MergedCell TxtCell
	 * TxtCell MergedCell MergedCell MergedCell TxtCell
	 * TxtCell TxtCell    TxtCell    TxtCell    TxtCell
	 */
	export class LogicTable {

		txtTable: TxtTable;
		logicRows: LogicRow[];

		constructor(txtTable: TxtTable) {
			this.txtTable = txtTable;
			this.logicRows = [];
			for (let row = DOM.findFirstChild(txtTable.table, IS_TxtRow); row; row = DOM.findNextSibling(row, IS_TxtRow)) {
				this.logicRows.push(new LogicRow(row, this.logicRows[this.logicRows.length - 1]));
			}
		}

		getLocicCellAt(logicRow: number, logicCol: number): TxtCell | MasterCell | MergedCell | null {
			const lRow = this.logicRows[logicRow];
			if (lRow) return lRow[logicCol];
			return null;
		}

		getTxtCellAt(logicRow: number, logicCol: number): TxtCell {
			const lRow = this.logicRows[logicRow];
			if (lRow) {
				const lCell = lRow[logicCol];
				if (lCell instanceof TxtCell) return lCell;
				if (lCell instanceof MasterCell) return lCell.txtCell;
				if (lCell instanceof MergedCell) return lCell.ownerCell.txtCell;
			}
			return null;
		}

		getLogicOffsets(cell: TxtCell): { offsRow: number, offsCol: number } {
			const row = cell.parentElement as TxtRow;
			for (let i = 0; i < this.logicRows.length; i++) {
				if (this.logicRows[i].txtRow === row) {
					const lRow = this.logicRows[i];
					for (let k = 0; k < lRow.length; k++) {
						const lCell = lRow[k];
						if (lCell === cell || (lCell instanceof MasterCell && lCell.txtCell === cell)) {
							return {offsRow: i, offsCol: k};
						}
					}
				}
			}
			return null;
		}

		getLogicRect(c1: TxtCell, c2: TxtCell): { startRow: number, startCol: number, endRow: number, endCol: number } {
			const p1 = this.getLogicOffsets(c1);
			const p2 = this.getLogicOffsets(c2);
			const r = {} as { startRow: number, startCol: number, endRow: number, endCol: number };
			r.startRow = Math.min(p1.offsRow, p2.offsRow);
			r.startCol = Math.min(p1.offsCol, p2.offsCol);
			r.endRow = Math.max(p1.offsRow + c1.getRowSpan(), p2.offsRow + c2.getRowSpan());
			r.endCol = Math.max(p1.offsCol + c1.getColSpan(), p2.offsCol + c2.getColSpan());
			return r;
		}

		getSelRect(): { startRow: number, startCol: number, endRow: number, endCol: number } {
			const t = this.txtTable.tableLayout;
			t.checkSel();
			return this.getLogicRect(t.focusCell, t.anchorCell);
		}

		insertRows(batch: IDocBatch, insOffset: number, rowM: TxtRowModel, count: number = 1) {
			if (!batch.selBefore) batch.setSelBefore((this.logicRows[insOffset] || this.logicRows[insOffset - 1]).txtRow.wedAnchor);
			let xa: IXAddr;
			const jmlCh: IJmlSet = [];
			let row = this.logicRows[insOffset];
			let extendSpan = true;
			if (row) {
				xa = row.txtRow.wedAnchor;
			} else {
				//on append à la fin
				extendSpan = false;
				row = this.logicRows[this.logicRows.length - 1];
				xa = XA.incrAtDepth(row.txtRow.wedAnchor, -1, 1);
			}
			for (let i = 0; i < row.length; i++) {
				const cell = row[i];
				if (cell instanceof TxtCell) {
					jmlCh.push(cell.model.newJml());
				} else if (cell instanceof MasterCell) {
					for (let i = 0; i < cell.colSpan; i++) jmlCh.push(cell.txtCell.model.newJml());
					i += cell.colSpan - 1;
				} else if (cell instanceof MergedCell && cell.ownerCell.rowSpan > cell.rowSpan) {
					const txtCell = cell.ownerCell.txtCell;
					if (extendSpan) {
						//rowSpan supérieurs : on incr leur rowSpan.
						txtCell.spanMgr.updateRowSpan(txtCell, cell.ownerCell.rowSpan + count, batch);
					} else {
						for (let i = 0; i < cell.colSpan; i++) jmlCh.push(txtCell.model.newJml());
					}
					i += cell.colSpan - 1;
				}
			}
			const jml: IJmlSet = [];
			for (let i = 0; i < count; i++) jml.push(rowM.newJml(), jmlCh);
			batch.insertJml(xa, jml);

			const selXa = XA.freeze(this.logicRows[insOffset] ? this.logicRows[insOffset].txtRow.wedAnchor : XA.incrAtDepth(this.logicRows[insOffset - 1].txtRow.wedAnchor, -1, 1));
			batch.setSelAfter(selXa, count > 1 ? XA.incrAtDepth(selXa, -1, count - 1) : null);
		}

		insertCols(batch: IDocBatch, insOffset: number, colM: TxtColModel, count: number = 1) {
			if (!batch.selBefore) batch.setSelBefore((this.txtTable.getCol(insOffset) || this.txtTable.getCol(insOffset - 1)).wedAnchor);
			let xa: IXAddr;
			const countCol = this.txtTable.countCols();
			if (insOffset >= countCol) {
				xa = XA.incrAtDepth(this.txtTable.getCol(countCol - 1).wedAnchor, -1, 1);
			} else {
				xa = this.txtTable.getCol(insOffset).wedAnchor;
			}

			//insertion des cells.
			for (let i = 0; i < this.logicRows.length; i++) {
				const row = this.logicRows[i];
				let cell = row[insOffset];
				if (cell) {
					if (cell instanceof MergedCell && cell.ownerCell.colSpan > cell.colSpan) {
						//On étend le span d'une cell existante.
						const txtCell = cell.ownerCell.txtCell;
						txtCell.spanMgr.updateColSpan(txtCell, cell.ownerCell.colSpan + count, batch);
						i += cell.ownerCell.rowSpan - 1;
					} else {
						let cellM: TxtCellModel;
						let offset = insOffset;
						let xa: IXAddr;
						if (cell instanceof MergedCell) {
							cellM = cell.ownerCell.txtCell.model;
							cell = row[--offset];
							while (cell instanceof MergedCell) cell = row[--offset];
							xa = XA.incrAtDepth(cell instanceof MasterCell ? cell.txtCell.wedAnchor : cell.wedAnchor, -1, 1);
						} else if (cell instanceof MasterCell) {
							xa = cell.txtCell.wedAnchor;
							cellM = cell.txtCell.model;
						} else {
							xa = cell.wedAnchor;
							cellM = cell.model;
						}
						const jml = [];
						for (let i = 0; i < count; i++) jml.push(cellM.newJml());
						batch.insertJml(xa, jml);
					}
				} else {
					//on append à la fin
					const lastCell = DOM.findLastChild(row.txtRow, IS_TxtCell);
					const cellM = this.getTxtCellAt(i, insOffset - 1).model;
					const jml = [];
					for (let i = 0; i < count; i++) jml.push(cellM.newJml());
					batch.insertJml(XA.incrAtDepth(lastCell.wedAnchor, -1, 1), jml);
				}
			}

			const colObj = colM.newJml() as IJmlObj;
			colM.widthMgr(this.txtTable.wedMgr).fillDefault(colM, colObj);
			const jml: IJmlSet = [];
			for (let i = 0; i < count; i++) jml.push(colObj);
			batch.insertJml(xa, jml);

			const selXa = XA.freeze(this.txtTable.countCols() > insOffset ? this.txtTable.getCol(insOffset).wedAnchor : XA.incrAtDepth(this.txtTable.getCol(insOffset - 1).wedAnchor, -1, 1));
			batch.setSelAfter(selXa, count > 1 ? XA.incrAtDepth(selXa, -1, count - 1) : null);
		}

		deleteRows(batch: IDocBatch, rowOffset: number, count: number = 1) {
			if (!batch.selBefore) batch.setSelBefore(this.logicRows[rowOffset].txtRow.wedAnchor, count > 1 ? this.logicRows[rowOffset + count - 1].txtRow.wedAnchor : null);
			//Gestion de la selAfter avant pour bénéficier des adjust.
			batch.setSelAfter((this.logicRows[rowOffset + count] || this.logicRows[rowOffset - 1]).txtRow.wedAnchor);

			//Cells à insérer dans la row suivante pour compenser des suppr de cellMasters avec rowSpan dépassant les rows supprimées
			let logicInsertInRowAfter: IJmlSet[];

			for (let r = rowOffset, m = rowOffset + count; r < m; r++) {
				const lRow = this.logicRows[r];
				//on recherche les rowSpan supérieurs pour decr leur rowSpan.
				for (let c = 0; c < lRow.length; c++) {
					const cell = lRow[c];
					if (r === rowOffset && cell instanceof MergedCell && cell.ownerCell.rowSpan > cell.rowSpan) {
						//1ère ligne
						//Une cell supérieure croise les lignes supprimées, on tronque le rowSpan
						const txtCell = cell.ownerCell.txtCell;
						txtCell.spanMgr.updateRowSpan(txtCell, cell.ownerCell.rowSpan - Math.min(cell.rowSpan, count), batch);
						c += cell.colSpan - 1;
					} else if (cell instanceof MasterCell && cell.rowSpan + r > m) {
						//Une cell avec rowSpan dépase des lignes qu'on veut supprimer, on doit réinjecter cette cell
						const newCell = cell.txtCell.spanMgr.splitCell(cell.txtCell, cell.rowSpan - m, cell.colSpan);
						(logicInsertInRowAfter || (logicInsertInRowAfter = []))[c] = newCell;
					}
				}
			}
			//Insertion des cells dépassant les lignes supprimées.
			if (logicInsertInRowAfter) {
				const lRowAfter = this.logicRows[rowOffset + count];
				for (let i = lRowAfter.length - 1; i >= 0; i--) {
					const lCell = lRowAfter[i];
					if (logicInsertInRowAfter[i] != null) {
						const cell = lRowAfter.getTxtCellBefore(i);
						batch.insertJml(cell ? XA.incrAtDepth(cell.wedAnchor, -1, 1) : XA.append(lRowAfter.txtRow.wedAnchor, lRowAfter.txtRow.getFirstChildXmlOffset()), logicInsertInRowAfter[i]);
					}
				}
			}
			//On suppr les rows
			batch.deleteSequence(this.logicRows[rowOffset].txtRow.wedAnchor, count);
		}

		deleteCols(batch: IDocBatch, colOffset: number, count: number = 1) {
			if (!batch.selBefore) batch.setSelBefore(this.txtTable.getCol(colOffset).wedAnchor);
			//Gestion de la selAfter avant pour bénéficier des adjust.
			batch.setSelAfter((this.txtTable.getCol(colOffset + count) || this.txtTable.getCol(colOffset - 1)).wedAnchor);
			//Parcours des rows pour suppr des cells
			for (let r = 0, m = this.logicRows.length; r < m; r++) {
				const lRow = this.logicRows[r];
				let cellToAddAfter: IJmlSet;
				let cellToDel = 0;
				for (let c = colOffset, end = colOffset + count; c < end; c++) {
					const lCell = lRow[c];
					if (lCell instanceof TxtCell) {
						cellToDel++;
					} else if (c === colOffset && lCell instanceof MergedCell && lCell.rowSpan === lCell.ownerCell.rowSpan) {
						//1ère colonne à suppr.
						//On est sur une mergedCell dont le master est avant sur notre row.
						const txtCellM = lCell.ownerCell.txtCell;
						txtCellM.spanMgr.updateColSpan(lCell.ownerCell.txtCell, lCell.ownerCell.colSpan - Math.min(lCell.colSpan, count), batch);
					} else if (lCell instanceof MasterCell) {
						cellToDel++;
						if (lCell.colSpan > end - c) {
							//On doit ajouter une cell à droite.
							cellToAddAfter = lCell.txtCell.spanMgr.splitCell(lCell.txtCell, lCell.rowSpan, lCell.colSpan - (end - c));
						}
					}
				}
				const prevCell = lRow.getTxtCellBefore(colOffset);
				const xaNext = prevCell ? XA.incrAtDepth(prevCell.wedAnchor, -1, 1) : XA.append(lRow.txtRow.wedAnchor, lRow.txtRow.getFirstChildXmlOffset());
				if (cellToDel > 0) batch.deleteSequence(xaNext, cellToDel);
				if (cellToAddAfter) batch.insertJml(xaNext, cellToAddAfter);
			}
			//suppr des balises de type col
			batch.deleteSequence(this.txtTable.getCol(colOffset).wedAnchor, count);
		}

		/** Merge / Split des cells. */
		mergeCells(batch: IDocBatch, txtCell: TxtCell, spanRow: number, spanCol: number) {
			const {offsRow, offsCol} = this.getLogicOffsets(txtCell);
			const originalRowSpan = txtCell.getRowSpan();
			const originalColSpan = txtCell.getColSpan();

			//Mise à jour des valeurs de span.
			if (originalRowSpan !== spanRow) txtCell.spanMgr.updateRowSpan(txtCell, spanRow, batch);
			if (originalColSpan !== spanCol) txtCell.spanMgr.updateColSpan(txtCell, spanCol, batch);

			const contentToAppend: IJmlSet = [];
			let logicInsertInRowAfter: IJmlSet[];

			// 1ère ligne
			let lRow = this.logicRows[offsRow];
			let deltaCol = spanCol - originalColSpan;
			if (deltaCol < 0) {
				//Unmerge de la 1ère ligne
				const cells: IJmlSet = [];
				const tpl = txtCell.spanMgr.splitCell(txtCell, 1, 1);
				for (let i = 0; i > deltaCol; i--) cells.push(...tpl);
				batch.insertJml(XA.incrAtDepth(txtCell.wedAnchor, -1, 1), cells);
			} else if (deltaCol > 0) {
				//Merge de la 1ère ligne (gestion des cassures d'autres fusions issues du dessus)
				let countCellToDel = 0;
				let cellToAddAfter: IJmlSet;
				for (let c = offsCol + originalColSpan; c < lRow.length && deltaCol > 0;) {
					const cell = lRow[c];
					if (cell instanceof TxtCell) {
						//on fusionne
						contentToAppend.push(...cell.exportContent());
						countCellToDel++;
					} else if (cell instanceof MergedCell) {
						if (cell.ownerCell.rowSpan !== cell.rowSpan) {
							//C'est un MergedCell issu d'un span d'une cell supérieure (qu'on va devoir tronquer, voire fragmenter)
							const cellToShrink = cell.ownerCell;
							if (spanRow < cell.rowSpan) {
								//On doit ajouter une cell en dessous
								const newCell = cellToShrink.txtCell.spanMgr.splitCell(cellToShrink.txtCell, cell.rowSpan - spanRow, cellToShrink.colSpan);
								(logicInsertInRowAfter || (logicInsertInRowAfter = []))[c] = newCell;
							}
							cellToShrink.txtCell.spanMgr.updateRowSpan(cellToShrink.txtCell, cellToShrink.rowSpan - cell.rowSpan, batch);
							if (deltaCol < cell.colSpan) {
								//On doit ajouter une cell à droite.
								cellToAddAfter = cellToShrink.txtCell.spanMgr.splitCell(cellToShrink.txtCell, Math.min(spanRow, cell.rowSpan), cell.colSpan - deltaCol);
							}
						} //else MergedCell issu d'un Master de notre ligne, donc déjà traité
					} else {
						//MasterCell dans notre 1ère ligne qu'on va absorber
						contentToAppend.push(...cell.txtCell.exportContent());
						countCellToDel++;
						if (spanRow < cell.rowSpan) {
							//On doit ajouter une cell en dessous
							const newCell = cell.txtCell.spanMgr.splitCell(cell.txtCell, cell.rowSpan - spanRow, cell.colSpan);
							(logicInsertInRowAfter || (logicInsertInRowAfter = []))[c] = newCell;
						}
						if (deltaCol < cell.colSpan) {
							//On doit ajouter une cell à droite.
							cellToAddAfter = cell.txtCell.spanMgr.splitCell(cell.txtCell, Math.min(spanRow, cell.rowSpan), cell.colSpan - deltaCol);
						}
					}
					c += cell.colSpan;
					deltaCol -= cell.colSpan;
				}
				const xaNext = XA.incrAtDepth(txtCell.wedAnchor, -1, 1);
				if (countCellToDel > 0) batch.deleteSequence(xaNext, countCellToDel);
				if (cellToAddAfter) batch.insertJml(xaNext, cellToAddAfter);
			}

			// Lignes suivantes
			let offsetRow = 1; //distance avec la ère ligne de notre cell
			lRow = this.logicRows[offsRow + offsetRow];
			while (lRow) {
				if (offsetRow < spanRow) {
					//On est dans la zone des rows encore ou nouvellement fusionnées
					//Merge de la ligne (gestion des cassures d'autres fusions issues de la gauche)
					let countCellToDel = 0;
					let cellToAddAfter: IJmlSet;
					let cellToMerge = spanCol;
					for (let c = offsCol; c < lRow.length && cellToMerge > 0;) {
						const cell = lRow[c];
						if (cell instanceof MergedCell) {
							if (c === offsCol) {
								//1ere celle de la ligne
								if (cell.ownerCell.txtCell === txtCell) {
									if (cellToMerge < cell.colSpan) {
										//On doit défusionner
										const cells: IJmlSet = [];
										const tpl = txtCell.spanMgr.splitCell(txtCell, 1, 1);
										for (let i = cell.colSpan - cellToMerge; i > 0; i--) cells.push(...tpl);
										const prevCell = lRow.getTxtCellBefore(offsCol);
										const xa = prevCell ? XA.incrAtDepth(prevCell.wedAnchor, -1, 1) : XA.append(lRow.txtRow.wedAnchor, lRow.txtRow.getFirstChildXmlOffset());
										batch.insertJml(xa, cells);
									}
								} else {
									//C'est un MergedCell issu d'un span d'une cell plus à gauche (qu'on va devoir tronquer, voire fragmenter)
									const cellToShrink = cell.ownerCell;
									if (spanRow - offsetRow < cell.rowSpan) {
										//On doit ajouter une cell en dessous
										const newCell = cellToShrink.txtCell.spanMgr.splitCell(cellToShrink.txtCell, cell.rowSpan - (spanRow - offsetRow), cellToShrink.colSpan);
										(logicInsertInRowAfter || (logicInsertInRowAfter = []))[c] = newCell;
									}
									cellToShrink.txtCell.spanMgr.updateColSpan(cellToShrink.txtCell, cellToShrink.colSpan - cell.colSpan, batch);
									if (cellToMerge < cell.colSpan) {
										//On doit ajouter une cell à droite.
										cellToAddAfter = cellToShrink.txtCell.spanMgr.splitCell(cellToShrink.txtCell, Math.min(spanRow - offsetRow, cell.rowSpan), cell.colSpan - cellToMerge);
									}
								}
							} //else MergedCell issu d'un Master déjà traité
						} else {
							contentToAppend.push(...(cell instanceof MasterCell ? cell.txtCell : cell).exportContent());
							countCellToDel++;
							if (cell instanceof MasterCell) {
								//MasterCell dans notre 1ère ligne qu'on va absorber
								if (spanRow - offsetRow < cell.rowSpan) {
									//On doit ajouter une cell en dessous
									const newCell = cell.txtCell.spanMgr.splitCell(cell.txtCell, cell.rowSpan - (spanRow - offsetRow), cell.colSpan);
									(logicInsertInRowAfter || (logicInsertInRowAfter = []))[c] = newCell;
								}
								if (cellToMerge < cell.colSpan) {
									//On doit ajouter une cell à droite.
									cellToAddAfter = cell.txtCell.spanMgr.splitCell(cell.txtCell, Math.min(spanRow - offsetRow, cell.rowSpan), cell.colSpan - cellToMerge);
								}
							}
						}
						cellToMerge -= cell.colSpan;
						c += cell.colSpan;
					}
					const prevCell = lRow.getTxtCellBefore(offsCol);
					const xaNext = prevCell ? XA.incrAtDepth(prevCell.wedAnchor, -1, 1) : XA.append(lRow.txtRow.wedAnchor, lRow.txtRow.getFirstChildXmlOffset());
					if (countCellToDel > 0) batch.deleteSequence(xaNext, countCellToDel);
					if (cellToAddAfter) batch.insertJml(xaNext, cellToAddAfter);
				} else if (offsetRow < originalRowSpan) {
					//On est dans la zone des rows qui ne sont plus fusionnées => unmerge
					if (logicInsertInRowAfter && offsetRow === spanRow) {
						// 1ère row après spanRow, on intègre les modifs dans la gestion de ce cas particulier.
						const newCell = txtCell.spanMgr.splitCell(txtCell, 1, 1);
						for (let i = 0; i < originalColSpan; i++) logicInsertInRowAfter[offsCol + i] = newCell;
					} else {
						const cells: IJmlSet = [];
						const tpl = txtCell.spanMgr.splitCell(txtCell, 1, 1);
						for (let i = 0; i < originalColSpan; i++) cells.push(...tpl);
						const prevCell = lRow.getTxtCellBefore(offsCol);
						const xaNext = prevCell ? XA.incrAtDepth(prevCell.wedAnchor, -1, 1) : XA.append(lRow.txtRow.wedAnchor, lRow.txtRow.getFirstChildXmlOffset());
						batch.insertJml(xaNext, cells);
					}
				} else {
					//on a traité toutes les rows concernées
					break;
				}
				//ligne suivante
				lRow = this.logicRows[++offsetRow + offsRow];
			}

			//Concat des contenus dans la cell principale.
			if (contentToAppend.length > 0) {
				batch.insertJml(XA.append(txtCell.wedAnchor, txtCell.getAppendXmlOffset()), contentToAppend);
			}

			//Insertion des cells fragmentées sur la ligne suivant le merge.
			if (logicInsertInRowAfter) {
				const lRowAfter = this.logicRows[offsRow + spanRow];
				for (let i = lRowAfter.length - 1; i >= 0; i--) {
					const lCell = lRowAfter[i];
					if (logicInsertInRowAfter[i] != null) {
						const cell = lRowAfter.getTxtCellBefore(i);
						batch.insertJml(cell ? XA.incrAtDepth(cell.wedAnchor, -1, 1) : XA.append(lRowAfter.txtRow.wedAnchor, lRowAfter.txtRow.getFirstChildXmlOffset()), logicInsertInRowAfter[i]);
					}
				}
			}
		}
	}

}

class LogicRow extends Array<TxtCell | MasterCell | MergedCell | null> {

	txtRow: TxtRow;

	protected withHoles: boolean;

	constructor(row: TxtRow, prev?: LogicRow) {
		super();
		this.txtRow = row;
		if (prev) for (let i = 0; i < prev.length; i++) {
			const c = prev[i];
			if (c instanceof MasterCell && c.rowSpan > 1) {
				this[i] = new MergedCell(c, c.rowSpan - 1, c.colSpan);
				this.withHoles = true;
			} else if (c instanceof MergedCell && c.rowSpan > 1) {
				this[i] = new MergedCell(c.ownerCell, c.rowSpan - 1, c.colSpan);
				this.withHoles = true;
			}
		}
		let cell = DOM.findFirstChild(row, TXTTABLE.IS_TxtCell);
		while (cell) {
			const rowSp = cell.getRowSpan();
			const colSp = cell.getColSpan();
			if (rowSp > 1 || colSp > 1) {
				const master = new MasterCell(cell, rowSp, colSp);
				this.addCell(master);
				for (let i = 1; i < colSp; i++) this.addCell(new MergedCell(master, rowSp, colSp - i));
			} else {
				this.addCell(cell);
			}
			cell = DOM.findNextSibling(cell, TXTTABLE.IS_TxtCell);
		}
	}

	addCell(cell: TxtCell | MasterCell | MergedCell) {
		if (this.withHoles) for (let i = 0; i < this.length; i++) {
			if (this[i] == null) {
				this[i] = cell;
				return;
			}
		}
		this.push(cell);
	}

	getTxtCellBefore(logicOffset: number): TxtCell {
		for (let i = logicOffset - 1; i >= 0; i--) {
			const cell = this[i];
			if (cell instanceof TxtCell) return cell;
			if (cell instanceof MasterCell) return cell.txtCell;
		}
		return null;
	}
}

//TODO transformer en JMasterCell ?
class MasterCell {
	/** Cellule réelle. */
	txtCell: TxtCell;
	/** Nb de rows (y compris celle-ci) qui sont fusionnées après cette cell. */
	rowSpan: number;
	/** Nb de cols (y compris celle-ci) qui sont fusionnées après cette cell. */
	colSpan: number;

	constructor(cell: TxtCell, rowSpan: number, colSpan: number) {
		this.txtCell = cell;
		this.rowSpan = rowSpan;
		this.colSpan = colSpan;
	}
}

//TODO transformer en JMergedCell ?
class MergedCell {
	/** Cellule absorbant cet espace. */
	ownerCell: MasterCell;
	/** Nb de rows (y compris celle-ci) qui sont fusionnées après cette cell. */
	rowSpan: number;
	/** Nb de cols (y compris celle-ci) qui sont fusionnées après cette cell. */
	colSpan: number;

	constructor(ownerCell: MasterCell, rowSpan: number, colSpan: number) {
		this.ownerCell = ownerCell;
		this.rowSpan = rowSpan;
		this.colSpan = colSpan;
	}
}


function newCellRange(bound1: TxtCell, bound2: TxtCell): IXAddrRange {
	if (bound1 === bound2) return {start: bound1.wedAnchor};
	const pos = bound1.compareDocumentPosition(bound2);
	if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return {start: bound1.wedAnchor, end: bound2.wedAnchor};
	return {start: bound2.wedAnchor, end: bound1.wedAnchor};
}
